/**
 * Medplum-backed loader / saver for Rules Engine PlanDefinitions.
 *
 * V1: immutable published versions, stable rule keys, draft-only deletes,
 * prior active packs with the same stable key retired on successful publish.
 * Validation enforced at persistence boundary before publish.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Bundle, PlanDefinition } from '@medplum/fhirtypes';


import { RE_CANONICAL_URL_PREFIX, type RulePackDraft, type RulesEngineData } from '../types';
import {
  dedupeLatestRuleVersionsForOverview,
  findHighestVersionForRuleKey,
  findSupersededActiveRulePacks,
  nextPublishedVersion,
  rulePackDraftToPlanDefinition,
  planDefinitionToRulePackDraft,
} from '../utils';
import { validateRulePackDraft } from '../utils/rulesEngineValidation';
import { UserFacingError } from '@/lib/userFacingError';

async function searchRulesEnginePlans(
  medplum: ReturnType<typeof useMedplum>,
): Promise<PlanDefinition[]> {
  // Try :contains first (works in Medplum cloud), fall back to broader search
  // filtered client-side if the modifier isn't supported.
  try {
    const bundle: Bundle = await medplum.search('PlanDefinition', {
      'url:contains': RE_CANONICAL_URL_PREFIX,
      _sort: '-_lastUpdated',
      _count: '100',
    });
    const results = (bundle.entry ?? [])
      .map((e) => e.resource as PlanDefinition | undefined)
      .filter((r): r is PlanDefinition => Boolean(r));
    if (results.length > 0) return results;
  } catch {
    // :contains modifier may not be supported on local Medplum
  }

  // Fallback: broader search filtered client-side by canonical URL prefix
  try {
    const bundle: Bundle = await medplum.search('PlanDefinition', {
      _sort: '-_lastUpdated',
      _count: '200',
    });
    return (bundle.entry ?? [])
      .map((e) => e.resource as PlanDefinition | undefined)
      .filter((r): r is PlanDefinition =>
        Boolean(r?.url?.startsWith(RE_CANONICAL_URL_PREFIX)),
      );
  } catch {
    return [];
  }
}

export interface UseRulesEngineResult {
  data: RulesEngineData;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  saveDraft: (draft: RulePackDraft) => Promise<PlanDefinition>;
  publish: (draft: RulePackDraft) => Promise<PlanDefinition>;
  retire: (id: string) => Promise<void>;
  deletePack: (id: string) => Promise<void>;
  editActiveAsDraft: (id: string) => RulePackDraft | null;
}

export function useRulesEngine(): UseRulesEngineResult {
  const medplum = useMedplum();

  const [data, setData] = useState<RulesEngineData>({ packs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const allPacks = await searchRulesEnginePlans(medplum);
      const packs = dedupeLatestRuleVersionsForOverview(allPacks);
      setData({ packs });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load rules engine data'));
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveDraft = useCallback(
    async (draft: RulePackDraft): Promise<PlanDefinition> => {
      const pd = rulePackDraftToPlanDefinition(draft, { status: 'draft' });
      const saved = draft.id
        ? await medplum.updateResource(pd)
        : await medplum.createResource(pd);
      await reload();
      return saved;
    },
    [medplum, reload],
  );

  const publish = useCallback(
    async (draft: RulePackDraft): Promise<PlanDefinition> => {
      // Enforce validation at persistence boundary
      const validation = validateRulePackDraft(draft);
      if (!validation.valid) {
        const summary = validation.errors.map((e) => `${e.path}: ${e.message}`).join('; ');
        throw new UserFacingError(`Publish validation failed: ${summary}`);
      }

      const stableKey = draft.stableRuleKey;
      let newVersion: string;

      if (stableKey) {
        const highestExisting = findHighestVersionForRuleKey(data.packs, stableKey);
        newVersion = nextPublishedVersion(highestExisting ?? draft.version);
      } else {
        newVersion = nextPublishedVersion(draft.version);
      }

      // Immutable publish: create a new active resource, never mutate existing
      const pd = rulePackDraftToPlanDefinition(
        { ...draft, id: undefined },
        { status: 'active', version: newVersion },
      );
      const created = await medplum.createResource(pd);

      const stableKeyForRetire = planDefinitionToRulePackDraft(created).stableRuleKey;
      if (created.id && stableKeyForRetire) {
        const superseded = findSupersededActiveRulePacks(data.packs, stableKeyForRetire, created.id);
        for (const prev of superseded) {
          if (!prev.id) continue;
          try {
            await medplum.updateResource({ ...prev, status: 'retired' });
          } catch {
            // Non-critical: superseded active cleanup failure
          }
        }
      }

      if (draft.id) {
        try {
          const existingPd = data.packs.find((p) => p.id === draft.id);
          if (existingPd && existingPd.status === 'draft') {
            await medplum.deleteResource('PlanDefinition', draft.id);
          }
        } catch {
          // Non-critical: old draft cleanup failure
        }
      }

      await reload();
      return created;
    },
    [medplum, reload, data.packs],
  );

  const retire = useCallback(
    async (id: string): Promise<void> => {
      const existing = data.packs.find((p) => p.id === id);
      if (!existing) return;
      await medplum.updateResource({ ...existing, status: 'retired' });
      await reload();
    },
    [medplum, reload, data.packs],
  );

  const deletePack = useCallback(
    async (id: string): Promise<void> => {
      const existing = data.packs.find((p) => p.id === id);
      if (existing && existing.status !== 'draft') {
        throw new UserFacingError('Only draft rules can be deleted. Retire active rules instead.');
      }

      await medplum.deleteResource('PlanDefinition', id);
      await reload();
    },
    [medplum, reload, data.packs],
  );

  const editActiveAsDraft = useCallback(
    (id: string): RulePackDraft | null => {
      const pd = data.packs.find((p) => p.id === id);
      if (!pd) return null;
      const draft = planDefinitionToRulePackDraft(pd);
      return {
        ...draft,
        id: undefined,
        status: 'draft',
        version: undefined,
      };
    },
    [data.packs],
  );

  return {
    data, loading, error, reload,
    saveDraft, publish, retire, deletePack,
    editActiveAsDraft,
  };
}
